import React, { Component } from 'react';
import { List, Icon } from 'semantic-ui-react';
import GoGaNarrative from '../GoGaNarrative';

export default class PayCalculationExplanation extends Component {
    
    getExplanationsByPractice = (explanations) => {
        if (!explanations || !Array.isArray(explanations)) {
            return {};
        }
        
        return explanations.reduce((groups, exp) => {
            const practice = exp.practiceName || exp.practiceId || 'General';
            if (!groups[practice]) {
                groups[practice] = [];
            }
            groups[practice].push(exp);
            return groups;
        }, {});
    }

    renderAllValues = (values) => {
        if (!values || typeof values !== 'object') {
            return null;
        }

        const renderValue = (key, value, level = 0) => {
            const indent = level * 15;
            const keyStyle = level === 0 ? { fontWeight: 'bold' } : {};
            
            if (value === null || value === undefined) {
                return null;
            }
            
            if (typeof value === 'object' && !Array.isArray(value)) {
                return (
                    <div key={key} style={{ marginLeft: `${indent}px`, marginBottom: '2px' }}>
                        <div style={{ ...keyStyle, marginBottom: '1px' }}>• {this.formatKey(key)}:</div>
                        {Object.entries(value).map(([subKey, subValue]) => 
                            renderValue(subKey, subValue, level + 1)
                        )}
                    </div>
                );
            }
            
            if (Array.isArray(value)) {
                return (
                    <div key={key} style={{ marginLeft: `${indent}px`, marginBottom: '2px' }}>
                        <div style={{ ...keyStyle, marginBottom: '1px' }}>• {this.formatKey(key)}:</div>
                        {value.map((item, index) => (
                            <div key={index} style={{ marginLeft: `${indent + 15}px`, fontSize: '0.95em' }}>
                                - {item}
                            </div>
                        ))}
                    </div>
                );
            }
            
            return (
                <div key={key} style={{ marginLeft: `${indent}px`, marginBottom: '1px' }}>
                    <span style={keyStyle}>• {this.formatKey(key)}:</span> {this.formatValue(value)}
                </div>
            );
        };

        return Object.entries(values).map(([key, value]) => renderValue(key, value));
    }

    formatKey = (key) => {
        return key
            .replace(/([A-Z])/g, ' $1') // Add space before capital letters
            .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
            .replace(/_/g, ' '); // Replace underscores with spaces
    }

    formatValue = (value) => {
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number') {
            return value;
        }
        return String(value);
    }

    renderExplanationStep = (step, index) => {
        if (step.goGaNarrative) {
            return (
                <List.Item key={index} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #eee' }}>
                    <List.Content>
                        <GoGaNarrative narrative={step.goGaNarrative} practiceName={step.practiceName} />
                    </List.Content>
                </List.Item>
            );
        }

        const hasValues = step.values && typeof step.values === 'object';
        const hasPayBreakdown = step.payBreakdown && typeof step.payBreakdown === 'object';
        
        return (
            <List.Item key={index} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #eee' }}>
                <List.Content>
                    <div style={{ fontSize: '0.9em', fontWeight: 'bold', marginBottom: '4px' }}>
                        {index + 1}. {step.stage}
                    </div>
                    <div style={{ fontSize: '0.85em', lineHeight: '1.4', marginBottom: '4px', color: '#333' }}>
                        {step.explanation}
                    </div>
                    
                    {hasValues && (
                        <div style={{ fontSize: '0.8em', color: '#666', marginLeft: '10px' }}>
                            {this.renderAllValues(step.values)}
                        </div>
                    )}
                    
                    {hasPayBreakdown && (
                        <div style={{ fontSize: '0.8em', marginTop: '4px', fontWeight: 'bold' }}>
                            Base: ${step.payBreakdown.basePay.toFixed(2)} + Bonus: ${step.payBreakdown.practiceBonus.toFixed(2)} + Notes: ${step.payBreakdown.financialNotes.toFixed(2)}
                        </div>
                    )}
                    
                    {step.result && (
                        <div style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#28a745', marginTop: '4px' }}>
                            Result: {step.result}
                        </div>
                    )}
                </List.Content>
            </List.Item>
        );
    }

    renderPracticeSection = (practiceName, explanations) => {
        const practiceTotal = explanations.find(exp => exp.stage === "Practice Total Pay Calculation");
        
        return (
            <div key={practiceName} style={{ 
                marginBottom: '25px', 
                border: '2px solid #2185d0',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#f8f9fb'
            }}>
                <div style={{ 
                    fontSize: '1.1em', 
                    fontWeight: 'bold', 
                    marginBottom: '12px', 
                    paddingBottom: '8px',
                    borderBottom: '2px solid #2185d0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#2185d0'
                }}>
                    <Icon name="building" size="large" />
                    <span>{practiceName}</span>
                    {practiceTotal && practiceTotal.result && (
                        <span style={{ 
                            fontSize: '0.9em', 
                            fontWeight: 'normal', 
                            color: '#28a745', 
                            marginLeft: '10px'
                        }}>
                            {practiceTotal.result}
                        </span>
                    )}
                </div>
                
                <List>
                    {explanations.map((step, index) => this.renderExplanationStep(step, index))}
                </List>
            </div>
        );
    }


    render() {
        const { doctorABBR, billingData, explanations: directExplanations, displayName } = this.props;

        let doctorExplanations = [];

        if (directExplanations && Array.isArray(directExplanations)) {
            doctorExplanations = directExplanations;
        } else if (billingData && billingData.groupByDoctorByPractice && billingData.groupByDoctorByPractice.length > 0) {
            const doctorData = billingData.groupByDoctorByPractice.find(doctor => doctor.ABBR === doctorABBR);
            if (doctorData && doctorData.payCalculationExplanations) {
                doctorExplanations = doctorData.payCalculationExplanations;
            }
        }

        const label = displayName || doctorABBR;

        if (!doctorExplanations || doctorExplanations.length === 0) {
            return (
                <div style={{ padding: '15px', textAlign: 'center', color: '#666' }}>
                    <p>No pay calculation explanations available for {label}.</p>
                    <p style={{ fontSize: '0.9em' }}>
                        This may be because the doctor has no calculations for this period or 
                        the explanations feature is not enabled for this contract type.
                    </p>
                </div>
            );
        }

        const practiceGroups = this.getExplanationsByPractice(doctorExplanations);
        const practiceTotals = doctorExplanations.filter(exp => exp.stage === "Practice Total Pay Calculation");
        const grandTotal = practiceTotals.reduce((sum, exp) => {
            return sum + (exp.payBreakdown ? exp.payBreakdown.totalPay : 0);
        }, 0);

        return (
            <div style={{ padding: '15px' }}>
                <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '15px', textAlign: 'center' }}>
                    Pay Calculation Breakdown for {label}
                </div>

                {/* Compact Summary */}
                <div style={{ padding: '10px', backgroundColor: '#f8f9fa', marginBottom: '15px', fontSize: '0.9em', textAlign: 'center' }}>
                    <strong>Summary:</strong> {Object.keys(practiceGroups).length} practice(s), {doctorExplanations.length} calculation steps
                    {grandTotal > 0 && <span> → <strong>Total Pay: ${grandTotal.toFixed(2)}</strong></span>}
                    {practiceTotals.length > 1 && (
                        <div style={{ fontSize: '0.85em', marginTop: '5px' }}>
                            {practiceTotals.map((total, index) => (
                                <span key={index} style={{ marginRight: '15px' }}>
                                    {total.practiceName}: ${total.payBreakdown.totalPay.toFixed(2)}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Practice-by-practice breakdown */}
                {Object.keys(practiceGroups).map(practiceName => 
                    this.renderPracticeSection(practiceName, practiceGroups[practiceName])
                )}
                
                <div style={{ textAlign: 'center', marginTop: '15px', fontSize: '0.8em', color: '#888' }}>
                    Step-by-step breakdown of how pay was calculated based on contract terms
                </div>
            </div>
        );
    }
}
